import { useState, useEffect } from 'react';
import styled from 'styled-components';
import Bugsnag from '@bugsnag/js';

import { AddressTextField } from 'checkout/components/AddressForm/AddressTextField';
import { FormikDropdownMenuSelector } from 'shared/components/DropdownMenuSelector';
import { Checkbox } from 'shared/components/Checkbox';
import { CnidInfoModal } from 'checkout/components/Tabs/CnidInfoModal';
import { FieldType } from 'checkout/enums/FieldType';
import { InfoIcon } from 'shared/icons/InfoIcon';
import { requestService } from 'shared/utils/services/requestService';
import { t } from 'localization/utils/t';

const TP = 'checkout.components.AddressForm.AddressFormChina';

export const AddressFormChina = ({
  chinaDistrict,
  handleChinaCitizenIdCardChange,
  isCnidCardChecked,
  setChinaDistrict,
}) => {
  const [chinaCity, setChinaCity] = useState({ display: '', value: '' });
  const [chinaProvince, setChinaProvince] = useState({ display: '', value: '' });
  const [chinaRegions, setChinaRegions] = useState([]);
  const [displayCnidInfo, setDisplayCnidInfo] = useState(false);

  const fetchChinaRegions = async () => {
    if (chinaRegions.length > 0) return;

    try {
      const regions = await requestService({
        url: '/web-api/v1/regions/all?country_code=CN',
        method: 'GET',
      });

      setChinaRegions(regions);
    } catch (error) {
      Bugsnag.notify(error, (event) => {
        event.severity = 'error';
        event.context = 'checkout';
      });
    }
  };

  const getChinaProvincesFormatted = () => {
    if (chinaRegions.length === 0) return [];

    return chinaRegions.map((province) => ({ display: province?.name, value: province?.name }));
  };

  const getChinaCitiesFormatted = () => {
    if (chinaRegions.length === 0) return [];

    const province =
      chinaRegions.find((_province) => _province?.name === chinaProvince?.value) || {};

    return province?.cities?.map((city) => ({ display: city?.name, value: city?.name })) || [];
  };

  const getChinaDistrictsFormatted = () => {
    if (chinaRegions.length === 0) return [];

    const province =
      chinaRegions.find((_province) => _province?.name === chinaProvince?.value) || {};

    const city = province?.cities?.find((_city) => _city?.name === chinaCity?.value) || {};

    return (
      city?.districts?.map((district) => ({
        display: district?.name,
        value: district?.name,
        zipcode: district?.zipcode,
      })) || []
    );
  };

  const handleChinaProvinceSelect = (province) => {
    setChinaProvince(province);
    setChinaCity({ display: '', value: '' });
    setChinaDistrict({ display: '', value: '', zipcode: '' });
  };

  const handleChinaCitySelect = (city) => {
    setChinaCity(city);
    setChinaDistrict({ display: '', value: '', zipcode: '' });
  };

  const handleChinaDistrictSelect = (district) => {
    setChinaDistrict(district);
  };

  const handleDismissInfoModal = () => {
    setDisplayCnidInfo(false);
  };

  const handleInfoIconClick = () => {
    setDisplayCnidInfo(true);
  };

  useEffect(() => {
    void fetchChinaRegions();
  }, []);

  return (
    <>
      <AddressFormChina.ChinaCitizenIdCardWrapper>
        <Checkbox
          checked={isCnidCardChecked}
          id="citizen-id-card"
          label={t(`${TP}.citizenIdCard`, 'Citizen ID Card')}
          name="citizenIdCard"
          onChange={handleChinaCitizenIdCardChange}
        />
      </AddressFormChina.ChinaCitizenIdCardWrapper>

      {isCnidCardChecked && (
        <AddressFormChina.CardNumberWrapper>
          <AddressTextField
            type={FieldType.CustomerIdNumber}
            label={t(`${TP}.chineseIdCardNumber`, 'Chinese ID Card Number')}
            name="customerIdNumber"
            data-qa="checkout_chinese_id_card_number"
          />
          <AddressFormChina.InfoIcon onClick={handleInfoIconClick} />
        </AddressFormChina.CardNumberWrapper>
      )}

      <AddressFormChina.ChinaRegionField
        data-qa="checkout_shipping_china_province_selector"
        label={t(`${TP}.chinaProvinceLabel`, 'Province')}
        menuItems={getChinaProvincesFormatted()}
        name="state"
        onMenuItemClick={handleChinaProvinceSelect}
        selection={chinaProvince}
      />

      <AddressFormChina.ChinaRegionField
        data-qa="checkout_shipping_china_city_selector"
        label={t(`${TP}.chinaCityLabel`, 'City')}
        menuItems={getChinaCitiesFormatted()}
        name="city"
        onMenuItemClick={handleChinaCitySelect}
        selection={chinaCity}
      />

      <AddressFormChina.ChinaRegionField
        data-qa="checkout_shipping_china_district_selector"
        label={t(`${TP}.chinaDistrictLabel`, 'District')}
        menuItems={getChinaDistrictsFormatted()}
        name="district"
        onMenuItemClick={handleChinaDistrictSelect}
        selection={chinaDistrict}
      />

      <AddressTextField
        type={FieldType.StreetAddress}
        label={t(`${TP}.addressLine1Label`, 'Street Address')}
        name="address1"
        data-qa="checkout_shipping_address1"
      />

      <AddressTextField
        type={FieldType.UnitNumber}
        label={t(`${TP}.addressLine2ChinaLabel`, 'Apt/Suite (optional)')}
        name="address2"
        data-qa="checkout_shipping_address2"
      />

      <AddressTextField
        type={FieldType.Phone}
        label={t(`${TP}.phoneNumberLabelChina`, 'Phone Number (optional)')}
        name="phone"
        data-qa="checkout_shipping_address2"
      />

      {displayCnidInfo && (
        <CnidInfoModal onClose={handleDismissInfoModal} onOkClick={handleDismissInfoModal} />
      )}
    </>
  );
};

AddressFormChina.ChinaCitizenIdCardWrapper = styled.div`
  label {
    padding: 0px 0 12px;
  }
`;

AddressFormChina.CardNumberWrapper = styled.div`
  position: relative;
  margin: 0;
`;

AddressFormChina.ChinaRegionField = styled(FormikDropdownMenuSelector)`
  margin-top: 0;
  padding-bottom: 24px;
`;

AddressFormChina.InfoIcon = styled(InfoIcon)`
  cursor: pointer;
  position: absolute;
  right: 0px;
  top: 2px;
`;
